<template>
  <Transition name="fade">
    <DrawerDialog
      v-if="changeFactoryModal"
      title="Update factory"
      :showCloseButton="true"
      :showFooter="true"
      :showSaveButton="true"
      :showCancelButton="true"
      saveButtonText="Update"
      cancelButtonText="Cancel"
      :sm="true"
      @closeEvent="closeEvent"
      @cancelEvent="closeEvent"
      @saveEvent="onSave"
      :saveButtonProps="{
        disabled: !isFormValid,
      }"
    >
      <template #modal-content>
        <template v-if="change_factory_for_all">
          <p class="mb-2">Select Factory:</p>
          <Select
            append-to-body
            v-model="selectedFactoryLocal"
            id="change_factory"
            name="change_factory"
            placeholder="Select Factory"
            label="name"
            track-by="id"
            :options="factories"
          />
          <div class="mt-4">
            <p class="mb-2">Select item status:</p>
            <Select
              append-to-body
              v-model="selectedOrderStatus"
              :options="moveOrderOptions"
              placeholder="Select Updated Order Status"
              track-by="value"
              label="text"
            />
          </div>
        </template>
        <template v-else-if="order && !change_factory_for_all">
          <div
            :class="factory_product_index == 0 ? 'mt-1' : 'mt-5'"
            v-for="(factory_product, factory_product_index) in order.factory_products"
            :key="factory_product.id"
          >
            <div>
              <p class="mb-2">
                {{
                  factory_product.is_new_product
                    ? factory_product.new_product_name
                    : factory_product.product_name
                }}
                - Move this product to:
                <template v-if="factory_product.reorder_data">
                  {{
                    `(Reorder from ${factory_product.reorder_data.order_number}: made at: ${factory_product.product_last_factory})`
                  }}
                </template>
              </p>
              <Select
                append-to-body
                v-model="factory_product.selected_factory_id"
                :options="factories"
                :placeholder="order.factory_name"
                track-by="id"
                label="name"
              />
            </div>
            <div class="mt-4">
              <p class="mb-2">Select item status:</p>
              <Select
                append-to-body
                v-model="factory_product.selected_status"
                placeholder="Select Updated Order Status"
                :options="moveOrderOptions"
                track-by="value"
                label="text"
              />
            </div>
          </div>
        </template>
        <div
          v-if="show_all_items_option"
          class="mt-4 pt-3 border-t border-[#e8e8e8]"
        >
          <input type="checkbox" v-model="change_factory_for_all" class="mt-0 align-middle" id="applyAll" />
          <label for="applyAll" class="ml-2 cursor-pointer align-middle">Apply to all items</label>
        </div>
        <div v-if="!eachFactoryRowHasStatusWhenFactorySelected()" class="text-[red] mt-2">
          <template v-if="change_factory_for_all">
            Select a factory and an item status.
          </template>
          <template v-else>
            Select an item status for every product where a factory is selected.
          </template>
        </div>
        <div
          v-else-if="!areStatusesConsistentForSameFactory()"
          class="text-[red] mt-2"
        >
          Products assigned to the same factory must have the same status.
        </div>
      </template>
    </DrawerDialog>
  </Transition>
</template>
<script lang="ts" setup>
import DrawerDialog from '@/components/Shared/DrawerDialog.vue';
import Select from '@/components/Shared/Select.vue';
import { showToast } from '@/composables/swal';
import { handleResponseException } from '@/interceptors/axiosRequestInterceptor';
import { DashboardItem, Factory, Order } from '@/models';
import { useMainStore } from '@/store/index';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
const props = defineProps<{
  currentTab: string;
  order_item: any;
}>();
/** More than one factory product: show bulk "Apply to all" (computed so it stays in sync with `order`). */
const show_all_items_option = computed(() => {
  const n =
    order.value?.factory_products?.length ??
    (props.order_item as DashboardItem | undefined)?.factory_products?.length ??
    0;
  return n > 1;
});
const selectedFactoryLocal = ref<Factory | null | undefined>();
const order = ref<DashboardItem | null>(null);
const selectedOrderId = ref<number | Order | null>(null);
const changeFactoryModal = ref<boolean>(false);

// const QUOTEREQUESTED = 'quote_requested';
// const QUOTEUPDATED = 'quote_updated';
// const QUOTEPROVIDED = 'quote_provided';
// const QUOTEREJECTED = 'quote_rejected';

const FACTORYREVIEW = 'submitted_for_factory_review';
const ORDERAPPROVE = 'order_approve';
const ORDERCANCEL = 'order_cancel';
const CUSTOMERREVIEW = 'submitted_for_customer_review';
// const CUSTOMERAPPROVED = 'customer_approved';
// const CUSTOMERREJECTED = 'customer_rejected';
const ORDERINPRODUCTION = 'in_production';
const ORDERSHIPPED = 'shipped';
const ORDERCOMPLETED = 'completed';

const change_factory_for_all = ref<boolean>(false);

const emit = defineEmits(['close', 'closeMenu']);

const selectedFactory = ref<Factory | null | undefined>(null);
const selectedOrderStatus = ref<Factory | null | undefined>(null);
const store = useMainStore();
const { factories } = storeToRefs(store);

const moveOrderOptions = [
  { value: null, text: 'Please select an option' },
  { value: FACTORYREVIEW, text: 'Order Created' },
  { value: ORDERAPPROVE, text: 'Mark to factory' },
  { value: ORDERCANCEL, text: 'Order Cancelled' },
  { value: CUSTOMERREVIEW, text: 'Customer Review' },
  { value: ORDERINPRODUCTION, text: 'In Production' },
  { value: ORDERSHIPPED, text: 'Order Shipped' },
  { value: ORDERCOMPLETED, text: 'Order Completed' },
];

/** Factory select may be a Factory object or a numeric id from the API. */
const resolveFactoryId = (sel: unknown): number | null => {
  if (sel == null) return null;
  if (typeof sel === 'number' && !Number.isNaN(sel)) return sel;
  if (typeof sel === 'object' && sel !== null && 'id' in sel) {
    const id = (sel as { id: unknown }).id;
    return typeof id === 'number' ? id : null;
  }
  return null;
};

/** Only include keys with real values (nothing null/undefined) for the API body. */
const compactProductFactoryFields = (
  factory_id: number | null,
  order_status: string | null | undefined
): Record<string, number | string> => {
  const out: Record<string, number | string> = {};
  if (factory_id != null) out.factory_id = factory_id;
  if (order_status != null && order_status !== undefined) out.order_status = order_status;
  return out;
};

const areStatusesConsistentForSameFactory = () => {
  if (!order.value || !order.value.factory_products) return true;
  const factoryStatusMap = new Map();
  for (const product of order.value.factory_products) {
    const factoryId = resolveFactoryId(product.selected_factory_id);
    const status = product.selected_status?.value;

    if (factoryId == null || status == null || status === undefined) {
      continue;
    }

    if (factoryStatusMap.has(factoryId)) {
      if (factoryStatusMap.get(factoryId) !== status) {
        return false;
      }
    } else {
      factoryStatusMap.set(factoryId, status);
    }
  }

  return true;
};

/** When a factory is chosen for a row, that row must also have a status (not the placeholder). */
const eachFactoryRowHasStatusWhenFactorySelected = () => {
  if (!order.value?.factory_products?.length) return true;
  if (change_factory_for_all.value) {
    const statusPick = selectedOrderStatus.value as { value?: string | null } | null | undefined;
    return (
      selectedFactoryLocal.value?.id != null &&
      statusPick != null &&
      statusPick.value != null &&
      statusPick.value !== undefined
    );
  }
  return order.value.factory_products.every((product) => {
    const factoryId = resolveFactoryId(product.selected_factory_id);
    const statusVal = product.selected_status?.value;
    if (statusVal != null && statusVal !== undefined && factoryId == null) return false;
    if (factoryId == null) return true;
    return statusVal != null && statusVal !== undefined;
  });
};

const isFormValid = computed(
  () =>
    eachFactoryRowHasStatusWhenFactorySelected() && areStatusesConsistentForSameFactory()
);

const closeEvent = () => {
  emit('close');
  emit("closeMenu")
  changeFactoryModal.value = false;
};
const validateFactoryProductWithStatus = (payload: any) => {
  const productFactoryIds = payload.product_factory_ids;
  const factoryStatusMap: Record<number, string> = {};

  for (const productId in productFactoryIds) {
    const entry = productFactoryIds[productId] as {
      factory_id?: number | null;
      order_status?: string | null;
    };
    const factory_id = entry.factory_id;
    const order_status = entry.order_status;

    if (factory_id == null && order.value) {
      const product = order.value.factory_products.find(
        (factory_product) => String(factory_product.id) === String(productId)
      );
      return {
        valid: false,
        message: `Factory is not selected for product ${product?.product_name}.`,
      };
    }

    if (order_status == null && order.value) {
      const product = order.value.factory_products.find(
        (factory_product) => String(factory_product.id) === String(productId)
      );
      return {
        valid: false,
        message: `Order status is not selected for product ${product?.product_name}.`,
      };
    }

    if (factory_id != null && order_status != null) {
      if (factoryStatusMap[factory_id] !== undefined) {
        if (factoryStatusMap[factory_id] !== order_status) {
          const currentFactory = factories.value.find((factory) => factory.id === factory_id);
          return {
            valid: false,
            message: `Order status for same factory ${currentFactory?.name} should be same.`,
          };
        }
      } else {
        factoryStatusMap[factory_id] = order_status;
      }
    }
  }

  return { valid: true, message: 'Validation passed.' };
};

const onSave = async (): Promise<void> => {
  if (order.value) {
    let payload: any = {
      order_id: order.value.order_id,
      order_item_id: order.value.id,
      product_factory_ids: {},
    };

    if (change_factory_for_all.value) {
      const bulkFactoryId = selectedFactoryLocal.value?.id ?? null;
      const bulkStatus =
        (selectedOrderStatus.value as { value?: string | null } | null | undefined)?.value ??
        null;
      order.value.factory_products.forEach((product) => {
        const fields = compactProductFactoryFields(bulkFactoryId, bulkStatus);
        if (Object.keys(fields).length > 0) {
          payload.product_factory_ids[product.id] = fields;
        }
      });
    } else {
      order.value.factory_products.forEach((product) => {
        const factoryId = resolveFactoryId(product.selected_factory_id);
        const orderStatus = product.selected_status?.value ?? null;
        const fields = compactProductFactoryFields(factoryId, orderStatus);
        if (Object.keys(fields).length > 0) {
          payload.product_factory_ids[product.id] = fields;
        }
      });
    }

    if (Object.keys(payload.product_factory_ids).length === 0) {
      showToast('Choose a factory and an item status for at least one product.', 'error');
      return;
    }
    const validationCheckForFactoryProductWithStatus = validateFactoryProductWithStatus(payload);
    if (!validationCheckForFactoryProductWithStatus.valid) {
      showToast(validationCheckForFactoryProductWithStatus.message, 'error');
      return;
    }

    try {
      const res = await store.updateOrderFactory(payload);
      if (res) {
        emit('close', true);
        changeFactoryModal.value = false;
      }
    } catch (e: any) {
      handleResponseException(e);
      emit('close');
      changeFactoryModal.value = false;
    } finally {
      // Close the right-click context menu only AFTER the update finished and
      // 'close' was emitted. Emitting closeMenu before the PUT resolved
      // unmounted DashboardActions (and this modal) early, so the dashboard
      // refresh (fetchOnSave -> invalidateQueries) was lost. (PBI #20041)
      emit('closeMenu');
    }
  }
};
const showUpdateFactoryModal = () => {
  const item = props.order_item as DashboardItem;
  if (factories.value.length > 0) {
    let factory = factories.value.find((factory: Factory) => item.factory_id === factory.id);
    order.value = JSON.parse(JSON.stringify(item));
    order.value = {
      ...order.value,
      factory_products: order.value
        ? order.value.factory_products?.map((product) => ({
            ...product,
            selected_factory_id: product.factory_id,
            selected_status: null,
          }))
        : [],
    } as DashboardItem;
    selectedOrderId.value = item.order_id;
    selectedFactory.value = factory;
    changeFactoryModal.value = true;
  } else {
    emit('close');
  }
};
onMounted(() => {
  showUpdateFactoryModal();
});
</script>
<style scoped lang="scss"></style>
